import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
  Skeleton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack,
  Description,
  ContentCopy,
  Download,
  CheckCircle,
  Cancel,
  Gavel,
  Block,
  Edit,
  Refresh,
  Pix,
  Person,
  CalendarToday,
  AttachMoney,
  Receipt,
  History,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

interface BoletoDetail {
  nossoNumero: number;
  seuNumero?: string;
  codigoBarras: string;
  linhaDigitavel: string;
  valor: number;
  dataVencimento: string;
  dataEmissao: string;
  dataLimitePagamento?: string;
  situacaoBoleto: string;
  codigoModalidade: number;
  codigoEspecieDocumento: string;
  tipoMulta?: number;
  valorMulta?: number;
  dataMulta?: string;
  tipoJurosMora?: number;
  valorJurosMora?: number;
  dataJurosMora?: string;
  tipoDesconto?: number;
  valorPrimeiroDesconto?: number;
  dataPrimeiroDesconto?: string;
  valorAbatimento?: number;
  pagador: {
    nome: string;
    numeroCpfCnpj: string;
    endereco?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
    email?: string;
  };
  beneficiarioFinal?: {
    nome: string;
    numeroCpfCnpj: string;
  };
  mensagensInstrucao?: string[];
  listaHistorico?: Array<{
    dataHistorico: string;
    tipoHistorico: string;
    descricaoHistorico: string;
  }>;
  qrCode?: string;
  pdfBoleto?: string;
}

const TIPO_MULTA = [
  { value: 0, label: 'Sem multa' },
  { value: 1, label: 'Valor fixo' },
  { value: 2, label: 'Percentual' },
];

const TIPO_JUROS = [
  { value: 0, label: 'Sem juros' },
  { value: 1, label: 'Valor por dia' },
  { value: 2, label: 'Taxa mensal' },
  { value: 3, label: 'Isento' },
];

const BoletoDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { nossoNumero } = useParams<{ nossoNumero: string }>();

  const [loading, setLoading] = useState(true);
  const [boleto, setBoleto] = useState<BoletoDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Dialog states
  const [baixarDialogOpen, setBaixarDialogOpen] = useState(false);
  const [alterarDialogOpen, setAlterarDialogOpen] = useState(false);
  const [protestoDialogOpen, setProtestoDialogOpen] = useState(false);
  const [negativacaoDialogOpen, setNegativacaoDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Alterar fields
  const [alterarTipo, setAlterarTipo] = useState<string>('');
  const [novaDataVencimento, setNovaDataVencimento] = useState<Date | null>(null);
  const [novaDataLimite, setNovaDataLimite] = useState<Date | null>(null);
  const [novoValor, setNovoValor] = useState('');
  const [tipoMultaAlterar, setTipoMultaAlterar] = useState(0);
  const [valorMultaAlterar, setValorMultaAlterar] = useState('');
  const [tipoJurosAlterar, setTipoJurosAlterar] = useState(0);
  const [valorJurosAlterar, setValorJurosAlterar] = useState('');
  const [valorAbatimentoAlterar, setValorAbatimentoAlterar] = useState('');

  // Protesto/Negativação fields
  const [diasProtesto, setDiasProtesto] = useState('30');
  const [diasNegativacao, setDiasNegativacao] = useState('60');

  useEffect(() => {
    if (nossoNumero) {
      loadBoleto();
    }
  }, [nossoNumero]);

  const loadBoleto = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/boletos?nossoNumero=${nossoNumero}`);
      if (response?.success && response.data) {
        setBoleto(response.data);
      } else {
        setError(response?.error?.message || 'Boleto não encontrado');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar boleto');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copiado!`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadSegundaVia = async () => {
    setActionLoading(true);
    try {
      const response = await api.get(`/boletos/${nossoNumero}/segunda-via?gerarPdf=true`);
      if (response?.success && response.data?.pdfBoleto) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${response.data.pdfBoleto}`;
        link.download = `boleto-${nossoNumero}.pdf`;
        link.click();
        toast.success('PDF baixado com sucesso!');
      } else {
        toast.error(response?.error?.message || 'Erro ao gerar segunda via');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao baixar PDF');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBaixar = async () => {
    setActionLoading(true);
    try {
      const response = await api.post(`/boletos/${nossoNumero}/baixar`, {
        codigoModalidade: boleto?.codigoModalidade || 1,
      });
      if (response?.success) {
        toast.success('Boleto baixado com sucesso!');
        setBaixarDialogOpen(false);
        loadBoleto();
      } else {
        toast.error(response?.error?.message || 'Erro ao baixar boleto');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao baixar boleto');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAlterar = async () => {
    if (!alterarTipo) {
      toast.error('Selecione o tipo de alteração');
      return;
    }

    setActionLoading(true);
    try {
      let updateData: any = {
        codigoModalidade: boleto?.codigoModalidade || 1,
      };

      switch (alterarTipo) {
        case 'vencimento':
          if (!novaDataVencimento) {
            toast.error('Informe a nova data de vencimento');
            setActionLoading(false);
            return;
          }
          updateData.prorrogacaoVencimento = {
            dataVencimento: format(novaDataVencimento, 'yyyy-MM-dd'),
          };
          break;
        case 'limitePagamento':
          if (!novaDataLimite) {
            toast.error('Informe a nova data limite');
            setActionLoading(false);
            return;
          }
          updateData.prorrogacaoLimitePagamento = {
            dataLimitePagamento: format(novaDataLimite, 'yyyy-MM-dd'),
          };
          break;
        case 'valor':
          if (!novoValor) {
            toast.error('Informe o novo valor');
            setActionLoading(false);
            return;
          }
          updateData.valorNominal = {
            valor: parseFloat(novoValor),
          };
          break;
        case 'multa':
          updateData.multa = {
            tipoMulta: tipoMultaAlterar,
            valorMulta: tipoMultaAlterar > 0 ? parseFloat(valorMultaAlterar) : 0,
          };
          break;
        case 'juros':
          updateData.jurosMora = {
            tipoJurosMora: tipoJurosAlterar,
            valorJurosMora: tipoJurosAlterar > 0 && tipoJurosAlterar !== 3 ? parseFloat(valorJurosAlterar) : 0,
          };
          break;
        case 'abatimento':
          updateData.abatimento = {
            valorAbatimento: parseFloat(valorAbatimentoAlterar) || 0,
          };
          break;
      }

      const response = await api.patch(`/boletos/${nossoNumero}`, updateData);
      if (response?.success) {
        toast.success('Boleto alterado com sucesso!');
        setAlterarDialogOpen(false);
        setAlterarTipo('');
        loadBoleto();
      } else {
        toast.error(response?.error?.message || 'Erro ao alterar boleto');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao alterar boleto');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProtestar = async () => {
    if (!diasProtesto || parseInt(diasProtesto) < 1) {
      toast.error('Informe o número de dias para protesto');
      return;
    }

    setActionLoading(true);
    try {
      const response = await api.post(`/boletos/${nossoNumero}/protestos`, {
        codigoModalidade: boleto?.codigoModalidade || 1,
        numeroDiasProtesto: parseInt(diasProtesto),
      });
      if (response?.success) {
        toast.success('Protesto solicitado com sucesso!');
        setProtestoDialogOpen(false);
        loadBoleto();
      } else {
        toast.error(response?.error?.message || 'Erro ao protestar boleto');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao protestar boleto');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelarProtesto = async () => {
    setActionLoading(true);
    try {
      const response = await api.patch(`/boletos/${nossoNumero}/protestos`, {
        codigoModalidade: boleto?.codigoModalidade || 1,
      });
      if (response?.success) {
        toast.success('Protesto cancelado com sucesso!');
        loadBoleto();
      } else {
        toast.error(response?.error?.message || 'Erro ao cancelar protesto');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao cancelar protesto');
    } finally {
      setActionLoading(false);
    }
  };

  const handleNegativar = async () => {
    if (!diasNegativacao || parseInt(diasNegativacao) < 1) {
      toast.error('Informe o número de dias para negativação');
      return;
    }

    setActionLoading(true);
    try {
      const response = await api.post(`/boletos/${nossoNumero}/negativacoes`, {
        codigoModalidade: boleto?.codigoModalidade || 1,
        numeroDiasNegativacao: parseInt(diasNegativacao),
      });
      if (response?.success) {
        toast.success('Negativação solicitada com sucesso!');
        setNegativacaoDialogOpen(false);
        loadBoleto();
      } else {
        toast.error(response?.error?.message || 'Erro ao negativar boleto');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao negativar boleto');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelarNegativacao = async () => {
    setActionLoading(true);
    try {
      const response = await api.patch(`/boletos/${nossoNumero}/negativacoes`, {
        codigoModalidade: boleto?.codigoModalidade || 1,
      });
      if (response?.success) {
        toast.success('Negativação cancelada com sucesso!');
        loadBoleto();
      } else {
        toast.error(response?.error?.message || 'Erro ao cancelar negativação');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao cancelar negativação');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const formatCpfCnpj = (doc: string) => {
    if (!doc) return '-';
    if (doc.length === 11) {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const getStatusChip = (status: string) => {
    const statusMap: Record<string, { color: 'success' | 'warning' | 'error' | 'info' | 'default'; label: string }> = {
      'Em Aberto': { color: 'warning', label: 'Em Aberto' },
      'Liquidado': { color: 'success', label: 'Pago' },
      'Baixado': { color: 'default', label: 'Baixado' },
      'Vencido': { color: 'error', label: 'Vencido' },
      'Em Protesto': { color: 'error', label: 'Em Protesto' },
      'Protestado': { color: 'error', label: 'Protestado' },
      'Negativado': { color: 'error', label: 'Negativado' },
    };
    const config = statusMap[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const canEdit = boleto?.situacaoBoleto === 'Em Aberto';

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  if (error || !boleto) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/boletos/list')} sx={{ mb: 2 }}>
          Voltar
        </Button>
        <Alert severity="error">
          <AlertTitle>Erro ao carregar boleto</AlertTitle>
          {error || 'Boleto não encontrado'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/boletos/list')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
            Boleto #{boleto.nossoNumero}
          </Typography>
          {getStatusChip(boleto.situacaoBoleto)}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadBoleto}>
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadSegundaVia}
            disabled={actionLoading}
          >
            Segunda Via (PDF)
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Dados do Boleto" icon={<Receipt />} iconPosition="start" />
        <Tab label="Pagador" icon={<Person />} iconPosition="start" />
        <Tab label="Histórico" icon={<History />} iconPosition="start" />
      </Tabs>

      {/* Tab: Dados do Boleto */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Info Principal */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Nosso Número</Typography>
                  <Typography variant="body1" fontWeight="bold">{boleto.nossoNumero}</Typography>
                </Grid>
                {boleto.seuNumero && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Seu Número</Typography>
                    <Typography variant="body1">{boleto.seuNumero}</Typography>
                  </Grid>
                )}
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Valor</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatCurrency(boleto.valor)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Vencimento</Typography>
                  <Typography variant="body1">{formatDate(boleto.dataVencimento)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Emissão</Typography>
                  <Typography variant="body1">{formatDate(boleto.dataEmissao)}</Typography>
                </Grid>
                {boleto.dataLimitePagamento && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Limite Pagamento</Typography>
                    <Typography variant="body1">{formatDate(boleto.dataLimitePagamento)}</Typography>
                  </Grid>
                )}
                {boleto.qrCode && (
                  <Grid item xs={12}>
                    <Chip icon={<Pix />} label="PIX habilitado" color="success" size="small" />
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Linha Digitável */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Linha Digitável
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  wordBreak: 'break-all',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{boleto.linhaDigitavel}</span>
                <Tooltip title="Copiar">
                  <IconButton size="small" onClick={() => handleCopy(boleto.linhaDigitavel, 'Linha digitável')}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Código de Barras
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  wordBreak: 'break-all',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{boleto.codigoBarras}</span>
                <Tooltip title="Copiar">
                  <IconButton size="small" onClick={() => handleCopy(boleto.codigoBarras, 'Código de barras')}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>

              {/* Encargos */}
              {(boleto.tipoMulta || boleto.tipoJurosMora || boleto.valorAbatimento) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Encargos
                  </Typography>
                  <Grid container spacing={2}>
                    {boleto.tipoMulta && boleto.tipoMulta > 0 && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Multa</Typography>
                        <Typography variant="body2">
                          {boleto.tipoMulta === 1 ? formatCurrency(boleto.valorMulta || 0) : `${boleto.valorMulta}%`}
                        </Typography>
                      </Grid>
                    )}
                    {boleto.tipoJurosMora && boleto.tipoJurosMora > 0 && boleto.tipoJurosMora !== 3 && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Juros</Typography>
                        <Typography variant="body2">
                          {boleto.tipoJurosMora === 1 ? `${formatCurrency(boleto.valorJurosMora || 0)}/dia` : `${boleto.valorJurosMora}% a.m.`}
                        </Typography>
                      </Grid>
                    )}
                    {boleto.valorAbatimento && boleto.valorAbatimento > 0 && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Abatimento</Typography>
                        <Typography variant="body2">{formatCurrency(boleto.valorAbatimento)}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {/* Mensagens */}
              {boleto.mensagensInstrucao && boleto.mensagensInstrucao.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Instruções
                  </Typography>
                  <Stack spacing={0.5}>
                    {boleto.mensagensInstrucao.map((msg, idx) => (
                      <Typography key={idx} variant="body2" color="text.secondary">
                        {idx + 1}. {msg}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}
            </Paper>
          </Grid>

          {/* Ações */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Ações
              </Typography>
              <Stack spacing={1.5}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => setAlterarDialogOpen(true)}
                  disabled={!canEdit}
                >
                  Alterar Boleto
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => setBaixarDialogOpen(true)}
                  disabled={!canEdit}
                >
                  Baixar (Cancelar)
                </Button>
                <Divider />
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  startIcon={<Gavel />}
                  onClick={() => setProtestoDialogOpen(true)}
                  disabled={!canEdit}
                >
                  Protestar
                </Button>
                {boleto.situacaoBoleto === 'Em Protesto' && (
                  <Button
                    fullWidth
                    variant="text"
                    color="warning"
                    onClick={handleCancelarProtesto}
                    disabled={actionLoading}
                  >
                    Cancelar Protesto
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Block />}
                  onClick={() => setNegativacaoDialogOpen(true)}
                  disabled={!canEdit}
                >
                  Negativar
                </Button>
                {boleto.situacaoBoleto === 'Negativado' && (
                  <Button
                    fullWidth
                    variant="text"
                    color="error"
                    onClick={handleCancelarNegativacao}
                    disabled={actionLoading}
                  >
                    Cancelar Negativação
                  </Button>
                )}
              </Stack>

              {!canEdit && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Ações disponíveis apenas para boletos em aberto.
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab: Pagador */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
            Dados do Pagador
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Nome</Typography>
              <Typography variant="body1">{boleto.pagador.nome}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">CPF/CNPJ</Typography>
              <Typography variant="body1">{formatCpfCnpj(boleto.pagador.numeroCpfCnpj)}</Typography>
            </Grid>
            {boleto.pagador.email && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">E-mail</Typography>
                <Typography variant="body1">{boleto.pagador.email}</Typography>
              </Grid>
            )}
            {boleto.pagador.endereco && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Endereço</Typography>
                <Typography variant="body1">
                  {boleto.pagador.endereco}
                  {boleto.pagador.bairro && `, ${boleto.pagador.bairro}`}
                  {boleto.pagador.cidade && ` - ${boleto.pagador.cidade}`}
                  {boleto.pagador.uf && `/${boleto.pagador.uf}`}
                  {boleto.pagador.cep && ` - CEP: ${boleto.pagador.cep}`}
                </Typography>
              </Grid>
            )}
          </Grid>

          {boleto.beneficiarioFinal && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Beneficiário Final
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Nome</Typography>
                  <Typography variant="body1">{boleto.beneficiarioFinal.nome}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">CPF/CNPJ</Typography>
                  <Typography variant="body1">{formatCpfCnpj(boleto.beneficiarioFinal.numeroCpfCnpj)}</Typography>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      )}

      {/* Tab: Histórico */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            <History sx={{ mr: 1, verticalAlign: 'middle' }} />
            Histórico de Movimentações
          </Typography>
          {boleto.listaHistorico && boleto.listaHistorico.length > 0 ? (
            <Stack spacing={2}>
              {boleto.listaHistorico.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{item.descricaoHistorico}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(item.dataHistorico)}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">Nenhuma movimentação registrada.</Typography>
          )}
        </Paper>
      )}

      {/* Dialog: Baixar Boleto */}
      <Dialog open={baixarDialogOpen} onClose={() => setBaixarDialogOpen(false)}>
        <DialogTitle>Baixar Boleto</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            <AlertTitle>Atenção</AlertTitle>
            Esta ação irá cancelar o boleto. O pagador não poderá mais efetuar o pagamento.
            Esta ação não pode ser desfeita.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBaixarDialogOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBaixar}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Cancel />}
          >
            Confirmar Baixa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Alterar Boleto */}
      <Dialog open={alterarDialogOpen} onClose={() => setAlterarDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Alterar Boleto</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Tipo de Alteração</InputLabel>
            <Select
              value={alterarTipo}
              label="Tipo de Alteração"
              onChange={(e) => setAlterarTipo(e.target.value)}
            >
              <MenuItem value="vencimento">Prorrogar Vencimento</MenuItem>
              <MenuItem value="limitePagamento">Prorrogar Limite de Pagamento</MenuItem>
              <MenuItem value="valor">Alterar Valor</MenuItem>
              <MenuItem value="multa">Alterar Multa</MenuItem>
              <MenuItem value="juros">Alterar Juros</MenuItem>
              <MenuItem value="abatimento">Conceder Abatimento</MenuItem>
            </Select>
          </FormControl>

          {alterarTipo === 'vencimento' && (
            <DatePicker
              label="Nova Data de Vencimento"
              value={novaDataVencimento}
              onChange={(v) => setNovaDataVencimento(v)}
              minDate={new Date()}
              slotProps={{ textField: { fullWidth: true } }}
            />
          )}

          {alterarTipo === 'limitePagamento' && (
            <DatePicker
              label="Nova Data Limite de Pagamento"
              value={novaDataLimite}
              onChange={(v) => setNovaDataLimite(v)}
              minDate={new Date()}
              slotProps={{ textField: { fullWidth: true } }}
            />
          )}

          {alterarTipo === 'valor' && (
            <TextField
              fullWidth
              label="Novo Valor"
              value={novoValor}
              onChange={(e) => setNovoValor(e.target.value)}
              type="number"
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
            />
          )}

          {alterarTipo === 'multa' && (
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Multa</InputLabel>
                <Select
                  value={tipoMultaAlterar}
                  label="Tipo de Multa"
                  onChange={(e) => setTipoMultaAlterar(e.target.value as number)}
                >
                  {TIPO_MULTA.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {tipoMultaAlterar > 0 && (
                <TextField
                  fullWidth
                  label={tipoMultaAlterar === 1 ? 'Valor da Multa (R$)' : 'Percentual da Multa (%)'}
                  value={valorMultaAlterar}
                  onChange={(e) => setValorMultaAlterar(e.target.value)}
                  type="number"
                />
              )}
            </Stack>
          )}

          {alterarTipo === 'juros' && (
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Juros</InputLabel>
                <Select
                  value={tipoJurosAlterar}
                  label="Tipo de Juros"
                  onChange={(e) => setTipoJurosAlterar(e.target.value as number)}
                >
                  {TIPO_JUROS.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {tipoJurosAlterar > 0 && tipoJurosAlterar !== 3 && (
                <TextField
                  fullWidth
                  label={tipoJurosAlterar === 1 ? 'Valor por dia (R$)' : 'Taxa mensal (%)'}
                  value={valorJurosAlterar}
                  onChange={(e) => setValorJurosAlterar(e.target.value)}
                  type="number"
                />
              )}
            </Stack>
          )}

          {alterarTipo === 'abatimento' && (
            <TextField
              fullWidth
              label="Valor do Abatimento"
              value={valorAbatimentoAlterar}
              onChange={(e) => setValorAbatimentoAlterar(e.target.value)}
              type="number"
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              helperText="Informe 0 para remover o abatimento"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlterarDialogOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAlterar}
            disabled={actionLoading || !alterarTipo}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            Confirmar Alteração
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Protesto */}
      <Dialog open={protestoDialogOpen} onClose={() => setProtestoDialogOpen(false)}>
        <DialogTitle>Protestar Boleto</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            <AlertTitle>Atenção</AlertTitle>
            O protesto será enviado ao cartório após os dias informados.
            O pagador será notificado e poderá ter o nome protestado.
          </Alert>
          <TextField
            fullWidth
            label="Dias para Protesto"
            value={diasProtesto}
            onChange={(e) => setDiasProtesto(e.target.value)}
            type="number"
            inputProps={{ min: 1 }}
            helperText="Número de dias após o vencimento para protestar"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProtestoDialogOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleProtestar}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Gavel />}
          >
            Confirmar Protesto
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Negativação */}
      <Dialog open={negativacaoDialogOpen} onClose={() => setNegativacaoDialogOpen(false)}>
        <DialogTitle>Negativar Boleto</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
            <AlertTitle>Atenção</AlertTitle>
            A negativação incluirá o pagador nos órgãos de proteção ao crédito (SPC/Serasa)
            após os dias informados.
          </Alert>
          <TextField
            fullWidth
            label="Dias para Negativação"
            value={diasNegativacao}
            onChange={(e) => setDiasNegativacao(e.target.value)}
            type="number"
            inputProps={{ min: 1 }}
            helperText="Número de dias após o vencimento para negativar"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNegativacaoDialogOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleNegativar}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Block />}
          >
            Confirmar Negativação
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoletoDetailPage;
